/**
 * How many template columns a sparse Pinterest grid should declare so leftover
 * horizontal space can be centered. Track size still comes from the full
 * viewport column count (`--games-grid-columns`); only the used count shrinks.
 */
export function usedPinterestGridColumns(
  contentColumnSpan: number,
  viewportColumns: number
): number {
  if (!Number.isFinite(viewportColumns) || viewportColumns < 1) {
    return 1;
  }
  if (!Number.isFinite(contentColumnSpan) || contentColumnSpan < 1) {
    return viewportColumns;
  }
  return Math.min(Math.floor(contentColumnSpan), Math.floor(viewportColumns));
}

/**
 * Columns left after side padding. 0.5 padding removes one column total
 * (half a column on each side). Never drops below one column.
 */
export function columnsAfterSidePadding(viewportColumns: number, sidePadding: number): number {
  const columns = Math.floor(viewportColumns);
  if (!Number.isFinite(columns) || columns < 1) return 1;
  const removed = Math.round(sidePadding * 2);
  if (!Number.isFinite(removed) || removed <= 0) return columns;
  return Math.max(1, columns - removed);
}

/** Sum of horizontal spans for every tile (one-row packing width). */
export function sumGridColumnSpans(columnSpans: readonly number[]): number {
  return columnSpans.reduce((total, span) => {
    const n = Number(span);
    if (!Number.isFinite(n) || n < 1) return total;
    return total + Math.floor(n);
  }, 0);
}
